import React, { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight, GripVertical, Pencil, Check, X } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { menuAPI } from '../../utils/api';
import { toast } from 'sonner';

// Sortable subcategory item
const SortableSubcategoryItem = ({ subcategory, categoryId, onEdit, onDelete, isEditing, editName, onEditNameChange, onSave, onCancel }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: subcategory.id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-2 rounded ${isEditing ? 'bg-yellow-50' : 'bg-secondary/20'}`}
    >
      {isEditing ? (
        <div className="flex gap-2 flex-1">
          <Input
            value={editName}
            onChange={(e) => onEditNameChange(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && onSave()}
            className="h-7 text-sm"
            autoFocus
          />
          <Button size="icon" className="h-7 w-7" onClick={onSave}>
            <Check className="h-3 w-3" />
          </Button>
          <Button size="icon" variant="outline" className="h-7 w-7" onClick={onCancel}>
            <X className="h-3 w-3" />
          </Button>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 flex-1">
            <button
              type="button"
              className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-gray-200 rounded touch-none"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-3 w-3 text-gray-400" />
            </button>
            <span className="text-sm">{subcategory.name}</span>
          </div>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => onEdit(subcategory)}
              title="Edit subcategory"
            >
              <Pencil className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-red-600"
              onClick={() => onDelete(subcategory.id)}
              title="Delete subcategory"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

const SortableCategoryItem = ({ category, isExpanded, onToggle, onAddSubcategory, onEdit, onDelete, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: category.id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  const subcategories = category.subcategories || [];

  return (
    <div ref={setNodeRef} style={style} className="border rounded-lg">
      <div className="flex items-center justify-between p-3 bg-secondary/30">
        <div className="flex items-center gap-2 flex-1">
          <button
            type="button"
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 rounded touch-none"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4 text-gray-400" />
          </button>
          <button
            onClick={onToggle}
            className="flex items-center gap-2 flex-1 text-left"
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            <span className="font-medium">{category.name}</span>
            {subcategories.length > 0 && (
              <span className="text-xs text-muted-foreground">
                ({subcategories.length} subcategories)
              </span>
            )}
          </button>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={onEdit}
            className="h-8 w-8"
            title="Edit category"
            data-testid={`edit-category-${category.id}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onDelete}
            className="h-8 w-8 text-red-600 hover:text-red-700"
            title="Delete category"
            data-testid={`delete-category-${category.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onAddSubcategory}
            className="h-8"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Sub
          </Button>
        </div>
      </div>
      {children}
    </div>
  );
};

export const CategoryWithSubcategories = ({ categories: initialCategories, onUpdate }) => {
  const [categories, setCategories] = useState(initialCategories);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [newSubcategoryName, setNewSubcategoryName] = useState('');
  const [addingTo, setAddingTo] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [editName, setEditName] = useState('');

  React.useEffect(() => {
    setCategories(initialCategories);
  }, [initialCategories]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const subcategorySensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = categories.findIndex((cat) => cat.id === active.id);
    const newIndex = categories.findIndex((cat) => cat.id === over.id);
    
    const newCategories = arrayMove(categories, oldIndex, newIndex);
    setCategories(newCategories);

    const reorderData = newCategories.map((cat, index) => ({
      id: cat.id,
      sort_order: index
    }));

    try {
      await menuAPI.reorderCategories(reorderData);
      toast.success('Category order updated');
      onUpdate();
    } catch (error) {
      console.error('Failed to reorder:', error);
      toast.error('Failed to save order');
      setCategories(initialCategories);
    }
  };

  const handleSubcategoryDragEnd = async (categoryId, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const category = categories.find(c => c.id === categoryId);
    if (!category) return;

    const subcategories = category.subcategories || [];
    const oldIndex = subcategories.findIndex((sub) => sub.id === active.id);
    const newIndex = subcategories.findIndex((sub) => sub.id === over.id);
    
    const newSubcategories = arrayMove(subcategories, oldIndex, newIndex);
    
    // Update local state
    setCategories(categories.map(cat => 
      cat.id === categoryId 
        ? { ...cat, subcategories: newSubcategories }
        : cat
    ));

    const reorderData = newSubcategories.map((sub, index) => ({
      id: sub.id,
      sort_order: index
    }));

    try {
      await menuAPI.reorderSubcategories(categoryId, reorderData);
      toast.success('Subcategory order updated');
      onUpdate();
    } catch (error) {
      console.error('Failed to reorder subcategories:', error);
      toast.error('Failed to save order');
      setCategories(initialCategories);
    }
  };

  const handleAddSubcategory = async (categoryId) => {
    if (!newSubcategoryName.trim()) {
      toast.error('Subcategory name is required');
      return;
    }

    try {
      await menuAPI.createSubcategory(categoryId, newSubcategoryName);
      toast.success('Subcategory added successfully');
      setNewSubcategoryName('');
      setAddingTo(null);
      onUpdate();
    } catch (error) {
      console.error('Failed to create subcategory:', error);
      toast.error(error.response?.data?.detail || 'Failed to create subcategory');
    }
  };

  const handleDeleteSubcategory = async (categoryId, subcategoryId) => {
    if (!window.confirm('Are you sure you want to delete this subcategory?')) return;

    try {
      await menuAPI.deleteSubcategory(categoryId, subcategoryId);
      toast.success('Subcategory deleted successfully');
      onUpdate();
    } catch (error) {
      console.error('Failed to delete subcategory:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete subcategory');
    }
  };

  const handleDeleteCategory = async (category) => {
    const message =
      `Delete category "${category.name}"?\n\n` +
      `Any menu items currently in this category will be unassigned ` +
      `(they will stay in the menu but won't belong to any category until you set one).`;
    if (!window.confirm(message)) return;

    try {
      const response = await menuAPI.deleteCategory(category.id);
      const unassigned = response?.data?.unassigned_items ?? 0;
      toast.success(
        unassigned > 0
          ? `Category deleted. ${unassigned} item${unassigned === 1 ? '' : 's'} unassigned.`
          : 'Category deleted successfully'
      );
      onUpdate();
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const startEditCategory = (category) => {
    setEditingCategory(category.id);
    setEditName(category.name);
    setEditingSubcategory(null);
  };

  const startEditSubcategory = (categoryId, subcategory) => {
    setEditingSubcategory({ categoryId, subcategoryId: subcategory.id });
    setEditName(subcategory.name);
    setEditingCategory(null);
  };

  const cancelEdit = () => {
    setEditingCategory(null);
    setEditingSubcategory(null);
    setEditName('');
  };

  const saveEditCategory = async (categoryId) => {
    if (!editName.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      await menuAPI.updateCategory(categoryId, editName.trim());
      toast.success('Category updated successfully');
      setEditingCategory(null);
      setEditName('');
      onUpdate();
    } catch (error) {
      console.error('Failed to update category:', error);
      toast.error(error.response?.data?.detail || 'Failed to update category');
    }
  };

  const saveEditSubcategory = async () => {
    if (!editName.trim()) {
      toast.error('Subcategory name is required');
      return;
    }

    try {
      await menuAPI.updateSubcategory(
        editingSubcategory.categoryId,
        editingSubcategory.subcategoryId,
        editName.trim()
      );
      toast.success('Subcategory updated successfully');
      setEditingSubcategory(null);
      setEditName('');
      onUpdate();
    } catch (error) {
      console.error('Failed to update subcategory:', error);
      toast.error(error.response?.data?.detail || 'Failed to update subcategory');
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={categories.map(cat => cat.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {categories.map((cat) => {
            const isExpanded = expandedCategory === cat.id;
            const subcategories = cat.subcategories || [];
            const isAdding = addingTo === cat.id;
            const isEditingThisCategory = editingCategory === cat.id;

            return (
              <SortableCategoryItem
                key={cat.id}
                category={cat}
                isExpanded={isExpanded}
                onToggle={() => setExpandedCategory(isExpanded ? null : cat.id)}
                onAddSubcategory={() => {
                  setAddingTo(isAdding ? null : cat.id);
                  setExpandedCategory(cat.id);
                }}
                onEdit={() => startEditCategory(cat)}
                onDelete={() => handleDeleteCategory(cat)}
              >
                {/* Category Edit Mode */}
                {isEditingThisCategory && (
                  <div className="p-3 border-t bg-yellow-50">
                    <div className="flex gap-2">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && saveEditCategory(cat.id)}
                        className="h-8"
                        autoFocus
                      />
                      <Button size="icon" className="h-8 w-8" onClick={() => saveEditCategory(cat.id)}>
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="outline" className="h-8 w-8" onClick={cancelEdit}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {isExpanded && !isEditingThisCategory && (
                  <div className="p-3 space-y-2 border-t">
                    {isAdding && (
                      <div className="flex gap-2 mb-3 bg-blue-50 p-2 rounded">
                        <Input
                          placeholder="Subcategory name"
                          value={newSubcategoryName}
                          onChange={(e) => setNewSubcategoryName(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleAddSubcategory(cat.id)}
                          className="h-8"
                        />
                        <Button
                          size="sm"
                          onClick={() => handleAddSubcategory(cat.id)}
                          className="h-8"
                        >
                          Add
                        </Button>
                      </div>
                    )}

                    {subcategories.length === 0 && !isAdding ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No subcategories yet
                      </p>
                    ) : (
                      <DndContext 
                        sensors={subcategorySensors} 
                        collisionDetection={closestCenter} 
                        onDragEnd={(event) => handleSubcategoryDragEnd(cat.id, event)}
                      >
                        <SortableContext 
                          items={subcategories.map(sub => sub.id)} 
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-2">
                            {subcategories.map((sub) => {
                              const isEditingThisSub = editingSubcategory?.categoryId === cat.id && 
                                                        editingSubcategory?.subcategoryId === sub.id;
                              
                              return (
                                <SortableSubcategoryItem
                                  key={sub.id}
                                  subcategory={sub}
                                  categoryId={cat.id}
                                  isEditing={isEditingThisSub}
                                  editName={editName}
                                  onEditNameChange={setEditName}
                                  onEdit={(subcategory) => startEditSubcategory(cat.id, subcategory)}
                                  onDelete={(subcategoryId) => handleDeleteSubcategory(cat.id, subcategoryId)}
                                  onSave={saveEditSubcategory}
                                  onCancel={cancelEdit}
                                />
                              );
                            })}
                          </div>
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>
                )}
              </SortableCategoryItem>
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
};
